/**
 * Farm & Acre Co - Form Validation
 *
 * Validates custom Allflex product fields before Add to Cart:
 * - Required field presence
 * - PIC format validation (4 letters + 4 numbers)
 * - Quantity min/step enforcement
 * - State compliance acknowledgments when required
 * - Inline error messages near each field
 * - Summary error at Add to Cart button
 *
 * Integrates with Dawn's product-form custom element.
 */

(function () {
  'use strict';

  var VALIDATION_RULES = {
    color: {
      required: true,
      type: 'radio',
      message: 'Please select a tag color',
    },
    'nlis-tag-type': {
      required: true,
      type: 'radio',
      message: 'Please select the NLIS tag type (Breeder or Post-Breeder)',
    },
    'print-layout': {
      required: true,
      message: 'Please select a printing option',
    },
    state: {
      required: true,
      message: 'Please select your state or territory',
    },
    pic: {
      required: true,
      pattern: /^[A-Z0-9\s]{8,10}$/i, // Allow 8-10 chars with optional spaces (cleaned in validateField)
      message: 'Please enter a valid 8-character PIC (e.g., NA123456, 3ABCD123, QA123456)',
    },
    'tas-pid': {
      // Tasmania-only Party ID. Gated by the live `required` flag (set by state-compliance.js only
      // when Tasmania is selected), so non-TAS orders skip it. Required presence only — no fixed format.
      required: true,
      message: 'Party ID (PID) is required for Tasmanian orders',
    },
    owner: {
      required: true,
      message: 'Please enter the registered owner name',
    },
    property: {
      required: true,
      message: 'Please enter the name to print on your tags (max 8 characters)',
    },
    'sa-levy': {
      required: true,
      type: 'checkbox',
      message: 'Please acknowledge the SA Biosecurity Levy',
    },
    'vic-deeca': {
      required: true,
      type: 'checkbox',
      message: 'Please acknowledge the DEECA requirement',
    },
    'qld-dpi': {
      required: true,
      type: 'radio',
      message: 'Please select your DPI consent status',
    },
    'qld-dpi-order': {
      required: true,
      message: 'Please enter your DPI Order Number',
    },
    'qld-dpi-expiry': {
      required: true,
      message: 'Please select the consent expiry date',
    },
    species: {
      required: true,
      message: 'Please select whether this is for sheep or goats',
    },
    'button-pairing': {
      required: true,
      type: 'radio',
      message: 'Please select whether you need matching buttons before continuing.',
    },
    // Visual-tag print-confirmation checkbox (replaces the removed free Start Number field).
    // Hard block: numbering counts as printing, so the customer must confirm the tag design
    // matches the paid print option. Only present on visual_tag products with a print section.
    'print-ack': {
      required: true,
      type: 'checkbox',
      message: 'Please confirm your print details match your selected print option before adding to cart',
    },
  };

  // --- Start Number <-> Print Style coordination (Issue 2) -------------------
  // A blank (unprinted) tag can't carry a starting number. These helpers keep the
  // Start Number field's `required` state in sync with the print selection, and power
  // the submit-time conflict check in validateForm(). Generic across the single-tag
  // form (data-allflex-field="print-style") and combos (print-style-female / -male).
  // Returns: true if any print-style select has a non-blank value, false if every
  // one is blank, or null when the form has no print-style select at all (e.g. sheep
  // EID/NLIS, which uses print-layout) — in which case the logic stays inert.
  function hasAnyPrintSelected(form) {
    var formId = form.getAttribute('id') || form.id;
    var selects = document.querySelectorAll(
      '[data-allflex-field^="print-style"][form="' + formId + '"], ' +
      'form#' + formId + ' [data-allflex-field^="print-style"]'
    );
    if (!selects.length) return null;
    for (var i = 0; i < selects.length; i++) {
      var v = selects[i].value;
      if (v && v !== 'blank') return true;
    }
    return false;
  }

  // Returns the data-allflex-field name(s) of every print-style select on this form, so
  // the blank+number conflict highlights the actual Print Style box(es). Single-tag form
  // -> ['print-style']; combo -> ['print-style-female','print-style-male'].
  function printStyleFields(form) {
    var formId = form.getAttribute('id') || form.id;
    var selects = document.querySelectorAll(
      '[data-allflex-field^="print-style"][form="' + formId + '"], ' +
      'form#' + formId + ' [data-allflex-field^="print-style"]'
    );
    var names = [];
    for (var i = 0; i < selects.length; i++) {
      var n = selects[i].getAttribute('data-allflex-field');
      if (n && names.indexOf(n) === -1) names.push(n);
    }
    return names;
  }

  // Start Number is required only when something is actually being printed.
  function syncStartNumberRequired(form) {
    var formId = form.getAttribute('id') || form.id;
    var startInput = document.querySelector(
      '[data-allflex-field="start-number"][form="' + formId + '"], ' +
      'form#' + formId + ' [data-allflex-field="start-number"]'
    );
    if (!startInput) return;
    var printed = hasAnyPrintSelected(form);
    if (printed === null) return; // no print-style select (sheep EID) — leave native required as-is
    if (printed) {
      startInput.setAttribute('required', 'required');
    } else {
      startInput.removeAttribute('required');
    }
  }

  function initFormValidation() {
    // Find all product forms on the page
    var productForms = document.querySelectorAll('product-form form');
    productForms.forEach(function (form) {
      attachValidation(form);
    });
  }

  function attachValidation(form) {
    var submitButton = form.closest('product-form').querySelector('[type="submit"]');
    if (!submitButton) return;

    // Intercept form submission
    form.addEventListener('submit', function (e) {
      var errors = validateForm(form);
      if (errors.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        displayErrors(errors, form);
        // Re-enable the submit button since Dawn's product-form disables it
        submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('loading');
        var spinner = form.closest('product-form').querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
        return false;
      } else {
        clearAllErrors(form);
      }
    }, true); // Use capture to run before Dawn's handler

    // Clear individual field errors on change
    form.querySelectorAll('[data-allflex-field]').forEach(function (field) {
      field.addEventListener('change', function () {
        clearFieldError(this.getAttribute('data-allflex-field'));
      });
      field.addEventListener('input', function () {
        clearFieldError(this.getAttribute('data-allflex-field'));
      });
    });

    // Also listen for fields outside the form but linked via form attribute
    document.querySelectorAll('[data-allflex-field][form]').forEach(function (field) {
      field.addEventListener('change', function () {
        clearFieldError(this.getAttribute('data-allflex-field'));
      });
    });

    // Issue 2: keep Start Number's required state in sync with the print selection —
    // required only when a print line/layout is chosen. Wire every print-style select
    // and run once now (print-style defaults to "blank", so a blank tag starts with no
    // number required).
    var syncFormId = form.getAttribute('id') || form.id;
    document.querySelectorAll(
      '[data-allflex-field^="print-style"][form="' + syncFormId + '"], ' +
      'form#' + syncFormId + ' [data-allflex-field^="print-style"]'
    ).forEach(function (sel) {
      sel.addEventListener('change', function () { syncStartNumberRequired(form); });
    });
    syncStartNumberRequired(form);

    // Issue 2 real-time: the moment a number is entered while nothing is printed, surface
    // the same highlight + message on the Print Style box(es) — don't wait for Add to Cart.
    var liveStart = document.querySelector(
      '[data-allflex-field="start-number"][form="' + syncFormId + '"], ' +
      'form#' + syncFormId + ' [data-allflex-field="start-number"]'
    );
    if (liveStart) {
      var reflectConflict = function () {
        var psNames = printStyleFields(form);
        if (!psNames.length) return; // sheep EID — inert
        if (hasAnyPrintSelected(form) === false && liveStart.value.trim() !== '') {
          displayErrors(psNames.map(function (n) {
            return { field: n, message: 'Because you have entered a starting number, your tags cannot be blank. Please pick at least one line of print here before adding to cart.' };
          }), form);
        } else {
          psNames.forEach(function (n) { clearFieldError(n); });
          var summary = form.closest('product-form').querySelector('.allflex-validation-summary');
          if (summary && !document.querySelector('.allflex-field--error')) summary.style.display = 'none';
        }
      };
      liveStart.addEventListener('input', reflectConflict);
      liveStart.addEventListener('change', reflectConflict);
      document.querySelectorAll(
        '[data-allflex-field^="print-style"][form="' + syncFormId + '"], ' +
        'form#' + syncFormId + ' [data-allflex-field^="print-style"]'
      ).forEach(function (sel) {
        sel.addEventListener('change', reflectConflict);
      });
    }
  }

  function validateForm(form) {
    var errors = [];
    var formId = form.getAttribute('id') || form.id;

    // Get all allflex fields (both inside form and linked via form attribute)
    var fields = document.querySelectorAll(
      '[data-allflex-field][form="' + formId + '"], form#' + formId + ' [data-allflex-field]'
    );

    var validatedFields = {};
    fields.forEach(function (field) {
      var fieldName = field.getAttribute('data-allflex-field');
      var rule = VALIDATION_RULES[fieldName];
      if (!rule) return;

      // Skip if we already validated this field (e.g. multiple radio buttons with same name)
      if (validatedFields[fieldName]) return;
      validatedFields[fieldName] = true;

      // Skip fields in hidden compliance sections
      var complianceParent = field.closest('.allflex-compliance');
      if (complianceParent && complianceParent.style.display === 'none') return;

      // Skip fields that aren't required
      if (!field.required && !field.hasAttribute('required')) return;

      var error = validateField(field, rule, fieldName);
      if (error) {
        errors.push(error);
      }
    });

    // Validate quantity
    var qtyError = validateQuantity(form);
    if (qtyError) errors.push(qtyError);

    // Issue 2 conflict: a starting number was entered but nothing is being printed.
    // Blank tags can't carry a number — block and highlight the PRINT STYLE box(es) (that
    // is what needs fixing). Inert on forms with no print-style select (sheep EID), where
    // hasAnyPrintSelected() returns null and printStyleFields() is empty.
    var startInput = document.querySelector(
      '[data-allflex-field="start-number"][form="' + formId + '"], ' +
      'form#' + formId + ' [data-allflex-field="start-number"]'
    );
    if (startInput && hasAnyPrintSelected(form) === false && startInput.value.trim() !== '') {
      printStyleFields(form).forEach(function (psName) {
        errors.push({
          field: psName,
          message: 'Because you have entered a starting number, your tags cannot be blank. Please pick at least one line of print here before adding to cart.'
        });
      });
    }

    return errors;
  }

  function validateField(field, rule, fieldName) {
    if (rule.type === 'checkbox') {
      if (!field.checked) {
        return { field: fieldName, message: rule.message };
      }
      return null;
    }

    if (rule.type === 'radio') {
      // Check if any radio in the group is selected
      var name = field.name;
      var anyChecked = document.querySelector('input[name="' + name + '"]:checked');
      if (!anyChecked) {
        return { field: fieldName, message: rule.message };
      }
      return null;
    }

    // Text/select fields
    var value = field.value.trim();
    if (rule.required && !value) {
      return { field: fieldName, message: rule.message };
    }

    // Special handling for PIC field (strip spaces, ensure 8 alphanumeric chars)
    if (fieldName === 'pic' && value) {
      var cleanedPIC = value.replace(/\s/g, '').toUpperCase();
      if (!/^[A-Z0-9]{8}$/.test(cleanedPIC)) {
        return { field: fieldName, message: rule.message };
      }
      // Update field with cleaned value
      field.value = cleanedPIC;
      return null;
    }

    if (rule.pattern && value && !rule.pattern.test(value)) {
      return { field: fieldName, message: rule.message };
    }

    return null;
  }

  function validateQuantity(form) {
    var formId = form.getAttribute('id') || form.id;
    var qtyInput = form.querySelector('.quantity__input') ||
                   document.querySelector('.quantity__input[form="' + formId + '"]');
    if (!qtyInput) return null;

    var value = parseInt(qtyInput.value) || 0;
    var min = parseInt(qtyInput.getAttribute('data-min') || qtyInput.min) || 1;

    if (value < min) {
      return { field: 'quantity', message: 'Minimum order quantity is ' + min };
    }

    return null;
  }

  function displayErrors(errors, form) {
    clearAllErrors(form);

    errors.forEach(function (error) {
      var errorEl = document.querySelector('[data-error-for="' + error.field + '"]');
      if (errorEl) {
        errorEl.textContent = error.message;
        errorEl.classList.add('allflex-field__error--visible');
      }

      // Add error class to the field container
      var fieldContainer = document.getElementById('field-' + error.field);
      if (fieldContainer) {
        fieldContainer.classList.add('allflex-field--error');
      }
    });

    // Show summary error near submit button
    var summaryEl = form.closest('product-form').querySelector('.allflex-validation-summary');
    if (!summaryEl) {
      summaryEl = document.createElement('div');
      summaryEl.className = 'allflex-validation-summary';
      var submitBtn = form.closest('product-form').querySelector('.product-form__buttons');
      if (submitBtn) {
        submitBtn.parentNode.insertBefore(summaryEl, submitBtn);
      }
    }

    summaryEl.innerHTML = '<div class="allflex-validation-summary__content">' +
      '<strong>Please complete the following:</strong>' +
      '<ul>' + errors.map(function (e) { return '<li>' + e.message + '</li>'; }).join('') + '</ul>' +
      '</div>';
    summaryEl.style.display = '';

    // Scroll to first error
    var firstErrorField = document.querySelector('.allflex-field--error');
    if (firstErrorField) {
      firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function clearAllErrors(form) {
    document.querySelectorAll('.allflex-field__error--visible').forEach(function (el) {
      el.textContent = '';
      el.classList.remove('allflex-field__error--visible');
    });
    document.querySelectorAll('.allflex-field--error').forEach(function (el) {
      el.classList.remove('allflex-field--error');
    });
    var summary = form.closest('product-form').querySelector('.allflex-validation-summary');
    if (summary) summary.style.display = 'none';
  }

  function clearFieldError(fieldName) {
    var errorEl = document.querySelector('[data-error-for="' + fieldName + '"]');
    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.remove('allflex-field__error--visible');
    }
    var container = document.getElementById('field-' + fieldName);
    if (container) {
      container.classList.remove('allflex-field--error');
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFormValidation);
  } else {
    initFormValidation();
  }
})();
